import { useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
// openlayers
import { Overlay } from 'ol';
import Feature from 'ol/Feature';
import Map from 'ol/Map';
import View from 'ol/View';
import { Coordinate } from 'ol/coordinate';
import { createEmpty, extend, isEmpty } from 'ol/extent';
import { Circle as CircleGeom, LineString, Point } from 'ol/geom';
import { Vector } from 'ol/layer';
import TileLayer from 'ol/layer/Tile';
import { fromLonLat, transform as projTransform, transform } from 'ol/proj';
import Projection from 'ol/proj/Projection';
import { register } from 'ol/proj/proj4';
import { TileImage, Vector as VectorSource, XYZ } from 'ol/source';
import { getLength } from 'ol/sphere';
import { Circle, Fill, Icon, Stroke, Style, Text } from 'ol/style';
import TileGrid from 'ol/tilegrid/TileGrid';
import proj4 from 'proj4';

import { getTideData, getWeatherData } from 'services/api-service/center';
import { MomentService } from 'services/moment-service';
import { utilService } from 'services/util-service';
import { IStoreState } from '../../store/modules';
import MapInformationOverlay from './MapInformationOverlay';
import MapRescueOverlay from './MapRescueOverlay ';

interface TideData {
  type: string;
  date: string;
  value: string;
}

interface MapProps {
  role?: string;
  phone?: string;
  participant?: string;
  location?: any;
  message?: any;
}

const MapRescue = ({ role, phone, participant, location, message }: MapProps) => {
  const [isOceanMap, setIsOceanMap] = useState(true);
  const [map, setMap] = useState<Map>();
  const [selectedCoord, setSelectedCoord] = useState<number[]>();
  const [selectedFeature, setSelectedFeature] = useState<string | undefined>(undefined);
  const locations = useSelector((store: IStoreState) => store.MainReducer.locations);
  const curRoom: any = useSelector((store: IStoreState) => store.MainReducer.curRoom);
  const [roomInfo, setRoomInfo] = useState({
    date: '',
    time: '',
    lat: '',
    lng: '',
  });
  const [tide, setTide] = useState<TideData[]>();
  const [curTideName, setCurTideName] = useState();
  const [weather, setWeather] = useState<any>();

  const mapElement = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map>();
  mapRef.current = map;

  const key = '0AE3197F-87CA-3304-8A9A-74CA8BB0E631';

  const OceanBaseMap = new TileLayer({
    className: 'OceanBaseMap',
    visible: true,
    // type: 'base',
    extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
    minResolution: 3.817573025390625,
    maxResolution: 1954.597389 + 250,
    // zIndex: 100,
    source: new XYZ({
      projection: new Projection({
        code: 'EPSG:5179',
        extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
        units: 'm',
      }),
      tileSize: 256,
      minZoom: 1,
      cacheSize: 10000,
      tileGrid: new TileGrid({
        extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
        origin: [-200000.0, -28024123.62],
        resolutions: [
          1954.597389, 977.2986945, 488.64934725, 244.324673625, 122.1623368125, 61.08116840625, 30.540584203125,
          15.2702921015625, 7.63514605078125, 3.817573025390625, 1.9087865126953125,
        ],
      }),
      tileUrlFunction: (tileCoord) => {
        let path = 'http://www.khoa.go.kr/safety_map/basemap.do?mapType=DL';
        const z = tileCoord[0] + 6;
        const x = Math.abs(tileCoord[1]);
        const y = Math.abs(tileCoord[2]) - 1;
        if (z < 10) {
          path += `&z=L0${z}&y=${y}&x=${x}`;
        } else {
          path += `&z=L${z}&y=${y}&x=${x}`;
        }
        return path;
      },
    }),
  });
  const OceanBaseView = new View({
    projection: new Projection({
      code: 'EPSG:5179',
      extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
      units: 'm',
    }),
    extent: [-200000.0, -28024123.62, 31824123.62, 4000000.0],
    enableRotation: false,
    resolutions: [
      1954.597389, 977.2986945, 488.64934725, 244.324673625, 122.1623368125, 61.08116840625, 30.540584203125,
      15.2702921015625, 7.63514605078125, 3.817573025390625, 1.9087865126953125,
    ],
    maxResolution: 1954.597389,
    zoomFactor: 1,
    center: [956498.5710969 + 70000, 1819967.0629328 + 20000],
    minZoom: 1,
    maxZoom: 9,
    zoom: 3,
  });
  const tms_resolution = [
    1954.597389, 977.2986945, 488.64934725, 244.324673625, 122.1623368125, 61.08116840625, 30.540584203125,
    15.2702921015625, 7.63514605078125, 3.817573025390625, 1.9087865126953125,
  ];
  const tms_extent = [531371.84, 967246.47, 1576674.68, 2274021.31];
  const m_extent = [-200000.0, -28024123.62, 31824123.62, 4000000.0];
  const org_extent = [37953.1466, 1126946.7253, 2152527.8074, 2873292.704];
  const projection = new Projection({
    code: 'EPSG:5179',
    extent: tms_extent,
  });
  const tileGrid = new TileGrid({
    extent: m_extent,
    origin: [m_extent[0], m_extent[1]],
    resolutions: tms_resolution,
  });
  const ENC57BaseMap = new TileLayer({
    className: 'ENC57BaseMap',
    visible: true,
    source: new TileImage({
      projection: projection,
      // tileSize: 256,
      tileGrid: tileGrid,
      tileUrlFunction: function (tileCoord) {
        const zoom = tileCoord[0] + 6;
        let zero = '';
        if (10 > zoom) {
          zero += '0';
        }
        const z = zero + zoom;
        const x = Math.abs(tileCoord[1]);
        const y = Math.abs(tileCoord[2]) - 1;
        return 'http://navada.kr/basemap/ENC/L' + z + '/' + x + '/' + y + '.png';
        // return 'http:///210.104.116.161/basemap/ENC/L' + z + '/' + x + '/' + y + '.png';
      },
    }),
  });
  const ENC57BaseView = new View({
    projection: projection,
    // restrictedExtent: org_extent,
    maxResolution: 1377.2986945,
    center: [956498.5710969, 1819967.0629328],
    zoom: 1,
    minZoom: 0,
    maxZoom: 12,
    enableRotation: false,
  });
  const vworldMap = new TileLayer({
    className: 'vworld_Base',
    visible: false,
    source: new XYZ({
      url: 'https://api.vworld.kr/req/wmts/1.0.0/' + key + '/Base/{z}/{y}/{x}.png',
    }),
  });
  const vworldView = new View({
    zoom: 9,
    minZoom: 0,
    maxZoom: 21,
    center: transform([127.7, 36.7], 'EPSG:4326', 'EPSG:900913'),
    constrainResolution: true,
    enableRotation: false,
  });

  useEffect(() => {
    proj4.defs(
      'EPSG:5179',
      '+proj=tmerc +lat_0=38 +lon_0=127.5 +k=0.9996 +x_0=1000000 +y_0=2000000 +ellps=GRS80 +units=m +no_defs'
    );
    register(proj4);

    const initialMap = new Map({
      target: mapElement.current!,
      layers: [ENC57BaseMap, vworldMap],
      view: ENC57BaseView,
      controls: [],
    });
    initialMap.on('click', handleMapClick);
    initialMap.on('dblclick', handleMapDbClick);
    setMap(initialMap);
    mapRef.current = initialMap;
    // Check if curRoom data already exists and display it
    if (curRoom !== undefined && curRoom.lat !== null && curRoom.lng !== null) {
      // Small timeout to ensure map is fully initialized
      setTimeout(() => {
        displayPoint(curRoom);
        getWeather(curRoom);
      }, 100);
    }
  }, []);

  useEffect(() => {
    createDrawLayer(locations);
  }, [locations]);

  useEffect(() => {
    if (message !== undefined) {
      const location = JSON.parse(decodeURIComponent(message.text));
      appendLocation(message.from.name(), location.role, location);
    }
  }, [message]);

  useEffect(() => {
    displayLocation(location);
    drawDistance(selectedFeature);
    if (selectedFeature !== undefined) {
      const loc = { lng: '', lat: '' };
      const result = locations.filter((location) => selectedFeature.includes(location.phone));
      if (!utilService.isEmpty(result)) {
        console.log('selectedFeature ', result[0].loc[result[0].loc.length - 1].lng);
        loc.lng = result[0].loc[result[0].loc.length - 1].lng;
        loc.lat = result[0].loc[result[0].loc.length - 1].lat;
        getWeather(loc);
      }
    }
  }, [location, selectedFeature]);

  useEffect(() => {
    if (curRoom !== undefined && curRoom.lat !== null && curRoom.lng !== null) {
      displayPoint(curRoom);
    }
  }, [curRoom]);

  const getWeather = async (info: any) => {
    if (role === 'V') return;
    try {
      const response: any = await getTideData(
        info,
        MomentService.getInstance().getDateDate(0),
        MomentService.getInstance().getDateDate(3)
      );
      if (response.data != null) {
        console.log('getWeather ', response.data);
        const tempData: TideData[] = [];
        setCurTideName(response.data.tide[0].moolNormal);
        if (response.data.tide[0].minTideDate1 != null) {
          tempData.push({
            type: '저조',
            date: response.data.tide[0].minTideDate1,
            value: response.data.tide[0].minTide1,
          });
        }
        if (response.data.tide[0].maxTideDate1 != null) {
          tempData.push({
            type: '고조',
            date: response.data.tide[0].maxTideDate1,
            value: response.data.tide[0].maxTide1,
          });
        }
        if (response.data.tide[0].minTideDate2 != null) {
          tempData.push({
            type: '저조',
            date: response.data.tide[0].minTideDate2,
            value: response.data.tide[0].minTide2,
          });
        }
        if (response.data.tide[0].maxTideDate2 != null) {
          tempData.push({
            type: '고조',
            date: response.data.tide[0].maxTideDate2,
            value: response.data.tide[0].maxTide2,
          });
        }
        setTide(
          tempData.sort(function (a: any, b: any) {
            return a.date - b.date;
          })
        );
      }
      console.log('getTideData ', response.data);
    } catch (error) {
      console.log('getTideData Error ', error);
    }
    try {
      const response: any = await getWeatherData(
        info,
        MomentService.getInstance().getDateDateTime(0),
        MomentService.getInstance().getDateDateTime(3)
      );
      if (response.data != null) {
        setWeather(response.data[0]);
      }
      console.log('getWeatherData ', response.data);
    } catch (error) {
      console.log('getWeatherData Error ', error);
    }
  };

  const changeMap = () => {
    console.log('changeMap', isOceanMap);
    if (mapRef.current !== undefined) {
      mapRef.current.getLayers().forEach(function (layer) {
        if (isOceanMap) {
          if (layer.getClassName() == 'vworld_Base') {
            layer.setVisible(true);
          }
          if (layer.getClassName() == 'ENC57BaseMap') {
            layer.setVisible(false);
          }
        } else {
          if (layer.getClassName() == 'vworld_Base') {
            layer.setVisible(false);
          }
          if (layer.getClassName() == 'ENC57BaseMap') {
            layer.setVisible(true);
          }
        }
      });
      setIsOceanMap((prevIsRunning) => !prevIsRunning);
    }
  };

  // map click handler
  const handleMapClick = (event) => {
    // console.log('handleMapClick', event);
    // get clicked coordinate using mapRef to access current React state inside OpenLayers callback
    //  https://stackoverflow.com/a/60643670
    // const clickedCoord = mapRef.current.getCoordinateFromPixel(event.pixel);
    // // transform coord to EPSG 4326 standard Lat Long
    // const transormedCoord = transform(clickedCoord, 'EPSG:3857', 'EPSG:4326');
    // // set React state
    // setSelectedCoord(transormedCoord);
    // if (mapRef.current !== undefined) {
    //   mapRef.current.forEachFeatureAtPixel(event.pixel, function (feature, layer) {
    //     const name = feature.get('id');
    //     if (feature !== undefined && name !== undefined) {
    //       setSelectedFeature(name);
    //     }
    //   });
    // }
    if (mapRef.current !== undefined) {
      const overlays = [...mapRef.current.getOverlays().getArray()];
      overlays.forEach((overlay) => {
        overlay.setMap(null);
      });
      mapRef.current.forEachFeatureAtPixel(
        event.pixel,
        function (feature, layer) {
          if (mapRef.current !== undefined) {
            const overlays = [...mapRef.current.getOverlays().getArray()];
            overlays.some((overlay) => {
              if (feature !== undefined && feature.get('type') === 'position') {
                if (overlay.getId() === 'Overlay_' + feature.get('data').phone) {
                  setSelectedFeature(feature.get('id'));
                  if (mapRef.current !== undefined) {
                    overlay.setMap(mapRef.current);
                    return true;
                  }
                }
              } else if (feature !== undefined && feature.get('id') === 'Overlay_Information') {
                if (overlay.getId() === 'Overlay_Information') {
                  setSelectedFeature(feature.get('id'));
                }
              }
            });
          }
        },
        {
          hitTolerance: 10,
        }
      );
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const handleMapDbClick = (event) => {
    if (mapRef.current !== undefined) {
      const clickedCoord = mapRef.current.getCoordinateFromPixel(event.pixel);
      const transormedCoord = transform(clickedCoord, 'EPSG:5179', 'EPSG:4326');
      setSelectedCoord(transormedCoord);
    }
  };

  const displayPoint = (curRoom: any) => {
    console.log('displayPoint ', mapRef.current);
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (mapRef.current !== undefined && layer && layer.getClassName() === 'Point_Layer') {
            mapRef.current.removeLayer(layer);
          }
        });
    }

    const vectorLayer = new Vector({
      className: 'Point_Layer',
      source: new VectorSource(),
      zIndex: 1000,
    });

    const iconStyle = [
      new Style({
        image: new Icon({
          src: require('../../assets/images/icon_point.png'),
          anchor: [0.5, 1.0],
        }),
        text: new Text({
          font: '12px Calibri,sans-serif',
          fill: new Fill({ color: 'red' }),
          stroke: new Stroke({
            color: 'red',
            width: 1,
          }),
          text: '사고위치',
          offsetY: 10,
        }),
      }),
    ];

    const features = new Feature({
      id: 'Overlay_Information',
      geometry: new Point(fromLonLat([curRoom.lng, curRoom.lat], 'EPSG:5179')),
    });

    features.setStyle(iconStyle);
    if (vectorLayer.getSource() !== null) {
      vectorLayer.getSource()?.addFeature(features);
    }

    for (let i = 1; i <= 5; i++) {
      const circle = new Feature({
        geometry: new CircleGeom(fromLonLat([curRoom.lng, curRoom.lat], 'EPSG:5179'), i * 500),
      });
      circle.setStyle(
        new Style({
          stroke: new Stroke({
            color: 'rgba(255, 0, 0, 0.8)',
            width: 2,
          }),
        })
      );
      if (vectorLayer.getSource() !== null) {
        vectorLayer.getSource()?.addFeature(circle);
      }
    }

    if (mapRef.current !== undefined) {
      mapRef.current.addLayer(vectorLayer);
      mapRef.current
        .getOverlays()
        .getArray()
        .filter((layer: any) => layer.id === 'Overlay_Information')
        .forEach((layer) => {
          if (mapRef.current !== undefined) {
            mapRef.current.removeOverlay(layer);
          }
        });

      // const container = document.createElement('div');
      // ReactDOM.createRoot(container).render(<MapInformationOverlay curRoom={curRoom} />);

      const overlay = new Overlay({
        id: 'Overlay_Information',
        // element: container,
        position: fromLonLat([curRoom.lng, curRoom.lat], 'EPSG:5179'),
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [5, -50],
      });

      mapRef.current.addOverlay(overlay);
      fitLocation();
    }
  };

  const checkExist = (phone: string) => {
    let returnValue = false;
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer) => {
          if (layer && layer.getClassName() === phone) {
            returnValue = true;
          }
        });
      return returnValue;
    }
  };

  const getFeatureMyStyle = (data: any) => {
    console.log('getFeatureMyStyle Data : ', data);
    return [
      new Style({
        image: new Icon({
          src: require('../../assets/images/icon_fishing.png'),
          color: data.role === 'R' ? '#0086FF' : '#FF0000',
          rotation: isNaN(data.heading) ? undefined : data.heading * 0.01745329251 /* Degree to Radian */,
          scale: 0.8,
        }),
        zIndex: 3,
      }),
    ];
  };

  const displayLocation = (location: any) => {
    if (location === undefined) {
      return;
    }
    if (checkExist('MyLocation')) {
      if (mapRef.current !== undefined) {
        mapRef.current
          .getLayers()
          .getArray()
          .slice()
          .forEach((layer: any) => {
            if (layer !== undefined && layer.getClassName() === 'MyLocation') {
              layer.getSource().forEachFeature(function (feature) {
                if (feature !== undefined && feature.get('name') === 'point') {
                  feature.setStyle(getFeatureMyStyle(location));
                  feature.getGeometry().setCoordinates(fromLonLat([location.lng, location.lat], 'EPSG:5179'));
                }
              });
            }
          });
      }
    } else {
      const marker = new Feature({
        name: 'point',
        geometry: new Point(fromLonLat([location.lng, location.lat], 'EPSG:5179')),
      });
      marker.setStyle(getFeatureMyStyle(location));
      const markerLayer = new Vector({
        className: 'MyLocation',
        source: new VectorSource({
          features: [marker],
        }),
      });
      if (mapRef.current !== undefined) {
        mapRef.current
          .getLayers()
          .getArray()
          .slice()
          .forEach((layer) => {
            if (layer && layer.getClassName() === 'MyLocation') {
              if (mapRef.current !== undefined) {
                mapRef.current.removeLayer(layer);
              }
            }
          });
        mapRef.current.addLayer(markerLayer);
        fitLocation();
        // mapRef.current.getView().fit(markerLayer.getSource()!.getExtent(), { duration: 1000, maxZoom: 6 });
      }
    }
  };

  const createDrawLayer = (location: any[]) => {
    console.log('createDrawLayer ', location, phone);
    for (let j = 0; j < location.length; j++) {
      if (checkExist(location[j].phone) || location[j].phone === phone) {
        continue;
      }
      const coords = fromLonLat([location[j].loc[0].lng, location[j].loc[0].lat], 'EPSG:5179');

      const startIconFeature = new Feature({
        type: 'startIcon',
        geometry: new Point(coords),
      });

      const gpxStartIconStyle = [
        new Style({
          image: new Icon({
            // anchor: [0.5, 0.5],
            opacity: 1,
            scale: 0.6,
            src:
              location[j].role === 'V'
                ? require('../../assets/images/icon_location_v.png')
                : require('../../assets/images/icon_location_r.png'),
          }),
          text: new Text({
            font: '10px Calibri,sans-serif',
            fill: new Fill({ color: '#000' }),
            stroke: new Stroke({
              color: location[j].role === 'V' ? '#e63528' : '#0086ff',
              // color: 'red',
              width: 2,
            }),
            text: location[j].role === 'V' ? '요구조자' : '구조대',
            offsetY: 12,
          }),
        }),
      ];

      const gpxPositionIconStyle = [
        new Style({
          image: new Icon({
            // anchor: [0.5, 0.5],
            opacity: 1,
            scale: 0.8,
            color: location[j].role === 'V' ? '#e63528' : '#0086ff',
            src: require('../../assets/images/icon_fishing.png'),
          }),
          text: new Text({
            font: '10px Calibri,sans-serif',
            fill: new Fill({ color: '#000' }),
            stroke: new Stroke({
              color: 'red',
              width: 2,
            }),
            offsetY: 12,
          }),
        }),
      ];

      const gpxStyles = {
        Point: new Style({
          image: new Circle({
            fill: new Fill({
              color: 'rgba(20, 140, 245, 0)',
            }),
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(20, 140, 245, 0)',
              width: 2,
            }),
          }),
        }),
        LineString: new Style({
          stroke: new Stroke({
            color: location[j].role === 'V' ? '#e63528' : '#0086ff',
            width: 3,
          }),
        }),
      };

      const gpxDrawLayer = new Vector({
        className: location[j].phone,
        source: new VectorSource({ features: [] }),
        style: (feature: any) => {
          if (feature.get('type') === 'startIcon') {
            return gpxStartIconStyle;
          } else if (feature.get('type') === 'position') {
            return gpxPositionIconStyle;
          } else {
            return gpxStyles[feature.getGeometry().getType()];
          }
        },
        zIndex: 2,
      });

      if (gpxDrawLayer !== null) {
        gpxDrawLayer.getSource()?.addFeature(new Feature({ name: 'line', geometry: new LineString(coords, 'XY') }));
        gpxDrawLayer.getSource()?.addFeature(startIconFeature);
      }

      if (mapRef.current !== undefined) {
        mapRef.current
          .getLayers()
          .getArray()
          .slice()
          .forEach((layer) => {
            if (layer && layer.getClassName() === location[j].phone) {
              if (mapRef.current !== undefined) {
                mapRef.current.removeLayer(layer);
              }
            }
          });
        mapRef.current.addLayer(gpxDrawLayer);
      }

      if (location[j].loc.length > 1) {
        let coords = [] as any;
        for (let i = 0; i < location[j].loc.length; i++) {
          coords = coords.concat(fromLonLat([location[j].loc[i].lng, location[j].loc[i].lat], 'EPSG:5179'));
        }
        if (gpxDrawLayer !== null) {
          // const geometry: Geometry | undefined = gpxDrawLayer.getSource()?.getFeatureById('line')?.getGeometry();
          // if (geometry instanceof LineString) {
          //   console.log('appendLocation LineString', coords);
          //   geometry.appendCoordinate(coords);
          // }
          gpxDrawLayer.getSource()!.forEachFeature(function (feature: any) {
            if (feature !== undefined && feature.get('name') === 'line') {
              feature.getGeometry().appendCoordinate(coords);
            }
          });
        }
      }
      appendLocation(location[j].phone, location[j].role, location[j].loc[location[j].loc.length - 1]);
      fitLocation();
    }
    // if (mapRef.current !== undefined) {
    //   mapRef.current
    //   .getView()
    //   .fit(gpxDrawLayer.getSource()!.getExtent(), { size: mapRef.current.getSize(), maxZoom: 18, duration: 2000 });
    // }
  };

  const appendLocation = (phone: string, role: string, loc: any) => {
    console.log('appendLocation', phone, role, loc);
    const coords = fromLonLat([loc.lng, loc.lat], 'EPSG:5179');
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (layer !== undefined && layer.getClassName() === phone) {
            layer.getSource().forEachFeature(function (feature) {
              if (feature !== undefined && feature.get('name') === 'line') {
                feature.getGeometry().appendCoordinate(coords);
                addLocationOverlay(feature, phone, role, loc);
                appendPosition(feature, phone, loc);
              }
            });
          }
        });

      if (location !== undefined) {
        // drawLinePath(loc);
      }
      drawPathPredic(phone, role, loc.lat, loc.lng, loc.speed, loc.heading);
    }
  };

  const appendPosition = (feature: any, phone: string, location: any) => {
    console.log('appendPosition', phone, location, locations);
    removedPosition(feature, phone, location);
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (mapRef.current !== undefined && layer && layer.getClassName().includes(phone)) {
            const feature = new Feature({
              id: 'Overlay_' + phone,
              type: 'position',
              data: { location, phone },
              geometry: new Point(fromLonLat([location.lng, location.lat], 'EPSG:5179')),
            });
            layer.getSource().addFeature(feature);
          }
        });
    }
  };

  const removedPosition = (feature: any, phone: string, location: any) => {
    console.log('removedPosition', phone, location, locations);
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (mapRef.current !== undefined && layer && layer.getClassName().includes(phone)) {
            const features = layer.getSource().getFeatures();
            if (features != null && features.length > 0) {
              for (const x in features) {
                const properties = features[x].getProperties();
                const type = properties.type;
                if (type == 'position') {
                  layer.getSource().removeFeature(features[x]);
                  break;
                }
              }
            }
          }
        });
    }
  };

  const drawLinePath = (loc: any) => {
    const source = new VectorSource();
    source.addFeature(
      new Feature({
        geometry: new LineString(
          fromLonLat([location.lng, location.lat], 'EPSG:5179').concat(fromLonLat([loc.lng, loc.lat], 'EPSG:5179')),
          'XY'
        ),
        name: 'LinePath',
      })
    );
    const pathLayer = new Vector({
      className: 'LinePath',
      source,
      style: new Style({
        stroke: new Stroke({
          color: '#0086ff',
          width: 3,
        }),
      }),
    });
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer) => {
          if (layer && layer.getClassName() === 'LinePath') {
            if (mapRef.current !== undefined) {
              mapRef.current.removeLayer(layer);
            }
          }
        });
      mapRef.current.addLayer(pathLayer);
    }
  };

  const drawPathPredic = (phone: string, role: string, lat: number, lng: number, spd: number, heading: number) => {
    const move_meter = spd * 60 * 5; //5분 예상 이동 거리(m 단위)
    const length_degree = 0.00001 * move_meter; //약 100m
    const direction_degree = heading;
    const lat_pred = lat + length_degree * Math.cos((direction_degree / 180) * Math.PI);
    const lng_pred = lng + length_degree * Math.sin((direction_degree / 180) * Math.PI);
    const source = new VectorSource();
    source.addFeature(
      new Feature({
        geometry: new LineString(fromLonLat([lng, lat]).concat(fromLonLat([lng_pred, lat_pred])), 'XY'),
        name: 'Predic_' + phone,
      })
    );
    const predicLayer = new Vector({
      className: 'Predic_' + phone,
      source,
      style: new Style({
        stroke: new Stroke({
          color: role === 'V' ? '#e63528' : '#0086ff',
          lineDash: [4, 8],
          width: 2,
        }),
      }),
    });
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer) => {
          if (layer && layer.getClassName() === 'Predic_' + phone) {
            if (mapRef.current !== undefined) {
              mapRef.current.removeLayer(layer);
            }
          }
        });
      mapRef.current.addLayer(predicLayer);
    }
  };

  const addLocationOverlay = (feature: any, phone: string, role: string, loc: any) => {
    if (loc !== undefined && loc.lng !== 0 && loc.lat !== 0) {
      if (mapRef.current !== undefined && feature) {
        // 2. Check Overlay
        const overlay = mapRef.current.getOverlayById('Overlay_' + phone);
        const distance =
          location === undefined ? '- 해리' : distanceBetweenPoints([location.lng, location.lat], [loc.lng, loc.lat]);
        if (overlay === null) {
          // 3. Create Feature
          mapRef.current
            .getLayers()
            .getArray()
            .filter((layer: any) => layer.getClassName() === phone)
            .forEach((layer: any) => {
              if (mapRef.current !== undefined && layer && layer.getClassName() === phone) {
                const feature = new Feature({
                  id: 'position',
                  type: 'position',
                  data: { phone, loc },
                  geometry: new Point(fromLonLat([loc.lng, loc.lat], 'EPSG:5179')),
                });
                feature.setStyle(getFeatureStyle(loc));
                layer.getSource().addFeature(feature);
              }
            });
          // 4. Add Overlay
          const container = document.createElement('div');
          container.innerHTML = ReactDOMServer.renderToStaticMarkup(
            <MapRescueOverlay phone={phone} role={role} location={loc} locations={locations} distance={distance} />
          );
          const overlay = new Overlay({
            id: 'Overlay_' + phone,
            element: container,
            position: fromLonLat([loc.lng, loc.lat], 'EPSG:5179'),
            positioning: 'bottom-center',
            offset: [0, -40],
          });
          container.addEventListener('click', function (e: Event) {
            const target = e.target as HTMLDivElement;
            if (target.className.includes('overlay-close')) {
              // onCloseOverlay(phone);
            } else if (target.className == 'overlay-cctv') {
              // console.log('Overlay cctv : ', target.className);
            }
          });
          mapRef.current.addOverlay(overlay);
          overlay.setMap(null);
        } else {
          // 5. Update Feature
          mapRef.current
            .getLayers()
            .getArray()
            .filter((layer: any) => layer.getClassName() === phone)
            .forEach((layer: any) => {
              if (mapRef.current !== undefined && layer && layer.getClassName() === phone) {
                const features = layer.getSource().getFeatures();
                if (features != null && features.length > 0) {
                  for (const x in features) {
                    const properties = features[x].getProperties();
                    const id = properties.id;
                    if (id == 'position') {
                      features[x].set('data', { phone, loc });
                      features[x].setGeometry(new Point(fromLonLat([loc.lng, loc.lat], 'EPSG:5179')));
                      break;
                    }
                  }
                }
              }
            });
          // 6. Update Overlay
          const element = overlay.getElement();
          if (element !== undefined) {
            element.innerHTML = ReactDOMServer.renderToStaticMarkup(
              <MapRescueOverlay phone={phone} role={role} location={loc} locations={locations} distance={distance} />
            );
            overlay.setElement(element);
          }
          overlay.setPosition(fromLonLat([loc.lng, loc.lat], 'EPSG:5179'));
        }
      }
    }
  };

  const drawDistance = (name: string | undefined) => {
    if (location === undefined || name === undefined) {
      return;
    }
    const loc: any = {};
    if (name === 'Overlay_Information') {
      loc.lng = curRoom.lng;
      loc.lat = curRoom.lat;
    } else {
      const result = locations.filter((location) => name.includes(location.phone));
      loc.lng = result[0].loc[result[0].loc.length - 1].lng;
      loc.lat = result[0].loc[result[0].loc.length - 1].lat;
    }
    const source = new VectorSource();
    source.addFeature(
      new Feature({
        geometry: new LineString(
          fromLonLat([location.lng, location.lat], 'EPSG:5179').concat(fromLonLat([loc.lng, loc.lat], 'EPSG:5179')),
          'XY'
        ),
        name: 'DistanceLine',
      })
    );
    const distance = distanceBetweenPoints([location.lng, location.lat], [loc.lng, loc.lat]);
    const pathLayer = new Vector({
      className: 'Distance',
      source,
      style: new Style({
        stroke: new Stroke({
          color: '#0086ff',
          width: 3,
        }),
        text: new Text({
          font: '14px Calibri,sans-serif',
          fill: new Fill({ color: '#fff' }),
          stroke: new Stroke({
            color: '#0086ff',
            width: 3,
          }),
          text: distance,
          offsetY: 15,
        }),
      }),
    });
    if (mapRef.current !== undefined) {
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer) => {
          if (layer && layer.getClassName() === 'Distance') {
            if (mapRef.current !== undefined) {
              mapRef.current.removeLayer(layer);
            }
          }
        });
      mapRef.current.addLayer(pathLayer);
    }
  };

  const transCoordsProj = (coord1, coord2?): Coordinate => {
    let ret: Coordinate = [];
    if (coord1[0] > 1000) {
      // 3857 to 4326
      ret = projTransform([coord1[0], coord1[1]], 'EPSG:3857', 'EPSG:4326');
      if (coord2) {
        const c2 = projTransform([coord2[0], coord2[1]], 'EPSG:3857', 'EPSG:4326');
        ret.push(c2[0]);
        ret.push(c2[1]);
      }
    } else {
      // 4326 to 3857
      ret = projTransform([coord1[0], coord1[1]], 'EPSG:4326', 'EPSG:3857');
      if (coord2) {
        const c2 = projTransform([coord2[0], coord2[1]], 'EPSG:4326', 'EPSG:3857');
        ret.push(c2[0]);
        ret.push(c2[1]);
      }
    }
    return ret;
  };

  const checkUndefined = (arr) => {
    return arr.includes(undefined);
  };

  const distanceBetweenPoints = (coord1, coord2) => {
    const line = new LineString([coord1, coord2], 'XY');
    const length = getLength(line, { projection: 'EPSG:4326' });
    // let output;
    // if (length > 100) {
    //   output = Math.round((length / 1000) * 100) / 100 + ' ' + 'km';
    // } else {
    //   output = Math.round(length * 100) / 100 + ' ' + 'm';
    // }
    const output = Math.round((length / 1000) * 100 * 0.539957) / 100 + ' ' + '해리';
    return output;
  };

  const getFeatureStyle = (data: any) => {
    return [
      new Style({
        image: new Icon({
          src: require('../../assets/images/icon_fishing.png'),
          color: '#FF0000',
          rotation: isNaN(data.heading) ? undefined : data.heading * 0.01745329251 /* Degree to Radian */,
          scale: 0.8,
        }),
        zIndex: 3,
      }),
    ];
  };

  const getImageSrc = (type) => {
    let imageSrc;
    switch (type) {
      case '비눈':
        imageSrc = require('../../assets/images/weather/icon_w_rainsnow.png');
        break;
      case '눈':
        imageSrc = require('../../assets/images/weather/icon_w_snow.png');
        break;
      case '비':
        imageSrc = require('../../assets/images/weather/icon_w_rain.png');
        break;
      case '흐림':
        imageSrc = require('../../assets/images/weather/icon_w_cloud.png');
        break;
      case '맑음':
      default:
        if (weather.fcstDate.substring(8, 10) >= 21 || weather.fcstDate.substring(8, 10) <= 6) {
          imageSrc = require('../../assets/images/weather/icon_w_night.png');
        } else {
          imageSrc = require('../../assets/images/weather/icon_w_sunny.png');
        }
        break;
      case '구름조금':
        if (weather.fcstDate.substring(8, 10) >= 21 || weather.fcstDate.substring(8, 10) <= 6) {
          imageSrc = require('../../assets/images/weather/icon_w_lowcloud_n.png');
        } else {
          imageSrc = require('../../assets/images/weather/icon_w_lowcloud.png');
        }
        break;
      case '구름많음':
        imageSrc = require('../../assets/images/weather/icon_w_highcloud.png');
        break;
    }
    return imageSrc;
  };

  const getImageTide = (type) => {
    let imageSrc;
    switch (type) {
      case '저조':
        imageSrc = require('../../assets/images/weather/icon_down.png');
        break;
      case '고조':
        imageSrc = require('../../assets/images/weather/icon_up.png');
        break;
    }
    return imageSrc;
  };

  const getDateStyles = (date: string) => {
    const month = date.substring(8, 10);
    const day = date.substring(10, 12);
    return month + ':' + day;
  };

  const fitLocation = () => {
    if (mapRef.current !== undefined) {
      const extent = createEmpty();
      mapRef.current
        .getLayers()
        .getArray()
        .slice()
        .forEach((layer: any) => {
          if (
            mapRef.current !== undefined &&
            !layer.getClassName().includes('Base') &&
            !layer.getClassName().includes('Predic_')
          ) {
            const source = layer.getSource();
            extend(extent, source.getExtent());
          }
        });
      if (!isEmpty(extent)) {
        mapRef.current.getView().fit(extent, {
          size: mapRef.current.getSize(),
          padding: [150, 100, 250, 100],
          maxZoom: 6,
          duration: 2000,
        });
      }
    }
  };

  return (
    <MapTemplateWrap>
      {curRoom !== undefined && curRoom.accLoc !== null && curRoom.accLoc !== null && (
        <MapInformationOverlay curRoom={curRoom} />
      )}
      <MapContainer ref={mapElement}></MapContainer>
      {/* <MapButton onClick={changeMap}>지도</MapButton> */}
      {role === 'R' && weather !== undefined && (
        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>날씨</th>
                <th>수온</th>
                <th>풍향</th>
                <th>풍속</th>
                {tide !== undefined &&
                  tide.map((item: any, index) => (
                    <th key={index}>
                      <IconWrap src={getImageTide(item.type)} alt="tide" />
                      {item.type}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <ImageWeather src={getImageSrc(weather.sky_ko)} alt="weather" />
                </td>
                <td>{weather.watertemp}℃</td>
                <td>
                  <IconWind itemProp={weather.wd}>
                    <ImageWind src={require('../../assets/images/weather/icon_wind01.png')} alt="wind" />
                  </IconWind>
                </td>
                <td>{weather.ws}m/s</td>
                {tide !== undefined &&
                  tide.map((item: any, index) => (
                    <td key={index}>
                      <div>{getDateStyles(item.date)}</div>
                      <div>{item.value}cm</div>
                    </td>
                  ))}
              </tr>
            </tbody>
          </table>
        </TableWrap>
      )}
    </MapTemplateWrap>
  );
};

const MapTemplateWrap = styled.div`
  width: 100%;
  height: 100%;
`;
const MapContainer = styled.div`
  width: 100%;
  height: 100%;
`;
const MapButton = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  width: 50px;
  height: 50px;
  border-radius: 10px;
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  color: #ffffff;
  background: #0086ff;
  border: none;
  cursor: pointer;
  z-index: 1;
`;
const IconWrap = styled.img`
  width: 15px;
  height: 15px;
`;
const ImageWeather = styled.img`
  width: 25px;
  height: 25px;
`;
const ImageWind = styled.img`
  width: 35px;
  height: 35px;
`;
const IconWind = styled.div`
  display: inline-block;
  width: 40px;
  height: 40px;
  vertical-align: middle;
  transform: ${(props) => 'rotateZ(' + (props.itemProp! + 180) + 'deg)'};
`;
const TableWrap = styled.div`
  position: absolute;
  bottom: 0;
  height: 4.1875rem;

  table {
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.6);
    border-spacing: 0;
    font-family: 'Inter';
    font-style: normal;
    font-weight: 500;
    font-size: 10px;
    line-height: 20px;
    align-items: center;
    text-align: center;

    thead {
      display: table;
      table-layout: fixed;
      width: 100%;
      font-family: 'Inter';
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 19px;
      align-items: center;
      text-align: center;
      color: #ffffff;
      // background: #5d5d5d;
      // border-radius: 30px 30px 0px 0px;
      th {
        vertical-align: middle;
        height: 1.75rem;
        // border: 1px solid #6b6b6b;
        color: #fff;
        font-family: Inter;
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
      }
    }

    tbody {
      height: calc(100% - 1.75rem);
      table-layout: fixed;
      display: block;
      overflow-y: auto;
      overflow-x: hidden;
      // margin: 0 5px 0 5px;
    }

    tr {
      display: table;
      width: 100%;
      table-layout: fixed;
      // border-bottom: 1px solid #6b6b6b;
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    td {
      vertical-align: middle;
      height: 2.4375rem;
      margin: 0;
      color: #fff;
      font-family: Inter;
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
      // border: 1px solid #6b6b6b;
      cursor: pointer;
      :last-child {
        border-right: 0;
      }
    }
  }
`;

export default MapRescue;
